import AppBackButton from '@nxsys/components/AppBackButton';
import AppButton from '@nxsys/components/AppButton';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import { useGoBackByQuery } from '@nxsys/hooks/useGoBackByQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch } from '@nxsys/store';
import { assignmentActions } from '@nxsys/store/slices/assignment/reducer/assignmentSlice';
import { commonActions } from '@nxsys/store/slices/common/reducer/commonSlice';
import { Space } from 'antd';
import moment from 'moment';
import { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import AssignmentForm, {
  AssignmentFormRef,
  AssignmentFormValues,
} from '../components/AssignmentForm';

const CreateAssignment = () => {
  const { messages } = useTypedIntl();
  const [searchParams] = useSearchParams();
  const defaultValuesStr = searchParams.get('defaultValues');
  const defaultValues = defaultValuesStr ? JSON.parse(defaultValuesStr) : null;

  const dispatch = useDispatch<AppDispatch>();
  const [isCreatingAssignment, setIsCreatingAssignment] =
    useState<boolean>(false);

  const formRef = useRef<AssignmentFormRef>(null);

  const { goBack } = useGoBackByQuery();

  const handleGoBack = () =>
    goBack({
      fromPathQueryKey: 'from',
      fallbackPathDefault: '/assignments',
      excludeParams: ['defaultValues'],
    });

  const renderTitle = useCallback(() => {
    return (
      <div className="flex items-center gap-x-4">
        <AppBackButton onClick={handleGoBack} />
        <div className="flex items-center gap-x-3">
          <AppPageTitle
            separator=""
            prefix={messages['assignment.addNew']}
            title=""
          />
        </div>
      </div>
    );
  }, [messages]);

  const renderRightContent = useCallback(() => {
    return (
      <div className="flex items-center justify-between">
        <Space>
          <AppButton
            variant="outlined"
            type="primary"
            name={messages['common.cancel'] || 'Cancel'}
            onClick={handleGoBack}
          />
          <AppButton
            type="primary"
            name={messages['common.save'] || 'Save'}
            onClick={() => {
              formRef.current?.submit();
            }}
          />
        </Space>
      </div>
    );
  }, [messages, formRef]);

  const handleSubmit = async (values: AssignmentFormValues) => {
    try {
      setIsCreatingAssignment(true);
      const params: any = {
        agencyRef: values.agencyName?.value,
        assignmentName: values.assignmentName ?? '',
        clientRef: values.clientRef ?? '',
        endDate: moment(values.endDate).toISOString(),
        invoicingAddress: {
          address: [
            values.invoicingAddress?.address1 ?? '',
            values.invoicingAddress?.address2 ?? '',
            values.invoicingAddress?.address3 ?? '',
          ],
          city: values.invoicingAddress?.city ?? '',
          county: values.invoicingAddress?.county ?? '',
          postCode: values.invoicingAddress?.postCode ?? '',
          country: values.invoicingAddress?.country?.value ?? '',
        },
        projectRef: values.projectRef ?? '',
        rates: values.rates?.map((rate) => {
          // fullData holds the selected PaymentRate entity (has id, type, code)
          const fullData = (rate as any).rateCode?.fullData;
          return {
            rateId: fullData?.id ?? '',
            rateCode: (rate.rateCode as any).value,
            rateType: fullData?.type || rate.rateType || '',
            description: rate.description,
            rate: rate.rate,
            vatRate: rate.vatRate,
            vatDetails: (rate.vatDetails as any).label,
            includeInCisCalculation: fullData?.includeInCisCalculation ?? false,
          };
        }),
        siteId: values.siteId?.value || null,
        startDate: moment(values.startDate).toISOString(),
        status: (values.status as any).value ?? '',
        subcontractorRef: values.employeeAndSubcontractor?.value ?? '',
        workLocation: {
          address: [
            values.workLocation?.address1 ?? '',
            values.workLocation?.address2 ?? '',
            values.workLocation?.address3 ?? '',
          ],
          city: values.workLocation?.city ?? '',
          county: values.workLocation?.county ?? '',
          postCode: values.workLocation?.postCode ?? '',
          country: (values.workLocation?.country as any)?.value ?? '',
        },
        file: null,
      };
      if ((values.attachment as File)?.name) {
        const res = await dispatch(
          commonActions.uploadFileToS3({ file: values.attachment as File }),
        ).unwrap();
        params.file = res.url;
      }
      await dispatch(assignmentActions.createAssignment(params)).unwrap();
      handleGoBack();
    } catch (error) {
      console.error(error);
    } finally {
      setIsCreatingAssignment(false);
    }
  };

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
      loading={isCreatingAssignment}
    >
      <AssignmentForm
        formRef={formRef}
        onSubmit={handleSubmit}
        {...(defaultValues ? { defaultValues } : {})}
      />
    </AppsContainer>
  );
};

export default CreateAssignment;
